import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import HeatLayer from "@/components/HeatLayer";
import { Flame } from "@phosphor-icons/react";

// prevent default icon 404s
delete L.Icon.Default.prototype._getIconUrl;

const centerFromDistrict = (d) => {
  if (d?.master_meters?.length) return [d.master_meters[0].lat, d.master_meters[0].lng];
  return [40.748, 14.640];
};

function NetworkMap({ metrics }) {
  const [ready, setReady] = useState(false);
  const [heatOn, setHeatOn] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);
  if (!ready || !metrics) return <div className="h-full grid place-items-center text-slate-500 font-mono text-sm">Loading map...</div>;

  const center = centerFromDistrict(metrics);

  return (
    <div className="relative h-full w-full map-vignette" data-testid="network-map">
      <div className="absolute top-3 right-3 z-[500] flex items-center gap-1 rounded-md border border-slate-700 bg-[#0a0f1c]/95 backdrop-blur-md p-1 shadow-lg">
        <button
          onClick={() => setHeatOn((v) => !v)}
          data-testid="heat-toggle-button"
          className={`px-2.5 py-1 rounded text-xs font-mono flex items-center gap-1.5 transition-colors ${
            heatOn ? "bg-[#EF4444]/15 text-[#EF4444]" : "text-slate-400 hover:text-slate-200"
          }`}
          title="Heat layer perdite"
        >
          <Flame size={12} weight={heatOn ? "fill" : "regular"} />
          Heat {heatOn ? "ON" : "OFF"}
        </button>
      </div>
      <MapContainer key="aquasave-map" center={center} zoom={15} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        {heatOn && <HeatLayer points={metrics.hotspots || []} />}
        {metrics.master_meters?.map((m) => (
          <CircleMarker
            key={m.id} center={[m.lat, m.lng]} radius={10}
            pathOptions={{ color: "#3EB6F0", weight: 2, fillColor: "#3EB6F0", fillOpacity: 0.85 }}
          >
            <Popup>
              <div className="font-mono text-xs">
                <div className="font-semibold text-[#3EB6F0]">{m.nome}</div>
                <div>Flusso: {m.flusso_lh} L/h</div>
                <div>Pressione: {m.pressione_bar} bar</div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
        {metrics.utenze?.map((u) => {
          const anom = u.allarme === "HIGH";
          return (
            <CircleMarker
              key={u.id} center={[u.lat, u.lng]} radius={7}
              pathOptions={{
                color: anom ? "#EF4444" : "#10B981",
                weight: 2,
                fillColor: anom ? "#EF4444" : "#10B981",
                fillOpacity: 0.9,
              }}
            >
              <Popup>
                <div className="font-mono text-xs space-y-1">
                  <div className="font-semibold" style={{color: anom?"#EF4444":"#10B981"}}>{u.id}</div>
                  <div>{u.indirizzo}</div>
                  <div>Flusso: {u.flusso_istantaneo_lh} L/h</div>
                  <div>Fatt.: {u.media_fatturata_lh} L/h</div>
                  <div>Z-Score: {u.z_score}</div>
                  <div>Stato: {u.stato_ai}</div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
        {metrics.tratti?.map((t) => (
          <Polyline
            key={t.id} positions={t.coords}
            pathOptions={{ color: t.colore, weight: 4, opacity: 0.85, dashArray: t.colore === "#EF4444" ? "8,6" : null }}
          >
            <Popup>
              <div className="font-mono text-xs">
                <div className="font-semibold">{t.nome}</div>
                <div>In: {t.ingresso_lh} L/h</div>
                <div>Out: {t.uscita_lh} L/h</div>
                <div>Perdita: <span style={{color:t.colore}}>{t.perdita_lh} L/h</span></div>
              </div>
            </Popup>
          </Polyline>
        ))}
      </MapContainer>
    </div>
  );
}

export default React.memo(NetworkMap, () => true);
